import { Webhook as WebhookIcon } from "@mui/icons-material";
import { Alert, Box } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { PageShell } from "@/components/common";
import { LoadingState } from "@/components/common/LoadingState";
import { WebhookForm, WebhookFormErrors } from "@/pages/admin/integrations/webhooks/WebhookForm";
import { Customer, StoreService } from "@/services/features/StoreService";
import {
  CustomerWebhookCreateRequest,
  WebhookEvent,
  WebhookService,
  WebhookType,
} from "@/services/features/WebhookService";

const CreateWebhook: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [formErrors, setFormErrors] = useState<WebhookFormErrors>({});
  const [formData, setFormData] = useState<CustomerWebhookCreateRequest>({
    customerId: 0,
    url: "",
    name: "",
    event: WebhookEvent.REQUEST_CREATE,
    type: WebhookType.HTTPS,
    headers: [],
    includeData: true,
    includeDemoStores: false,
  });

  const storeService = useMemo(() => StoreService.getInstance(), []);
  const webhookService = useMemo(() => WebhookService.getInstance(), []);
  const translateRef = useRef(t);

  useEffect(() => {
    translateRef.current = t;
  }, [t]);

  useEffect(() => {
    document.title = t("management.webhooks.createWebhook.documentTitle");
  }, [t]);

  useEffect(() => {
    const loadCustomers = async () => {
      setIsLoading(true);
      try {
        const response = await storeService.getCustomers();
        const availableCustomers = response.data ?? [];
        setCustomers(availableCustomers);

        if (customerId) {
          const requestedCustomerId = parseInt(customerId);
          const selectedCustomer = availableCustomers.find(
            (candidate) => candidate.id === requestedCustomerId,
          );
          if (selectedCustomer) {
            setCustomer(selectedCustomer);
            setFormData((current) => ({ ...current, customerId: requestedCustomerId }));
          } else {
            setError(
              translateRef.current("management.webhooks.createWebhook.errors.customerNotFound"),
            );
          }
        }
      } catch {
        setError(
          translateRef.current("management.webhooks.createWebhook.errors.failedLoadCustomers"),
        );
      } finally {
        setIsLoading(false);
      }
    };

    void loadCustomers();
  }, [customerId, storeService]);

  const validateForm = (): boolean => {
    const nextErrors: WebhookFormErrors = {};
    if (!formData.customerId) {
      nextErrors.customerId = t("management.webhooks.createWebhook.errors.customerRequired");
    }
    if (!formData.name.trim()) {
      nextErrors.name = t("management.webhooks.createWebhook.errors.nameRequired");
    }
    if (!formData.url.trim()) {
      nextErrors.url = t("management.webhooks.createWebhook.errors.urlRequired");
    }
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    setError(null);
    if (!validateForm()) return;

    setIsCreating(true);
    try {
      const response = await webhookService.createWebhook(formData);
      if (!response.success) {
        setError(
          response.message || t("management.webhooks.createWebhook.errors.failedCreateWebhook"),
        );
        return;
      }

      enqueueSnackbar(t("management.webhooks.createWebhook.messages.webhookCreated"), {
        variant: "success",
      });
      void navigate("/admin/integrations/webhooks");
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : t("management.webhooks.createWebhook.errors.failedCreateWebhook"),
      );
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <PageShell
        breadcrumbs="admin"
        title={t("management.webhooks.createWebhook.title")}
        icon={<WebhookIcon />}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <LoadingState label={t("common.loading")} />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.webhooks.createWebhook.title")}
      icon={<WebhookIcon />}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <WebhookForm
        data={formData}
        errors={formErrors}
        isEdit={false}
        isSubmitting={isCreating}
        submitLabel={t("management.webhooks.createWebhook.actions.createWebhook")}
        customers={customers}
        selectedCustomer={customer}
        onCustomerChange={(nextCustomer) => {
          setCustomer(nextCustomer);
          setFormData((current) => ({ ...current, customerId: nextCustomer?.id ?? 0 }));
          setFormErrors((current) => ({ ...current, customerId: undefined }));
        }}
        onFieldChange={(field, value) => {
          setFormData((current) => ({ ...current, [field]: value }));
          if (field === "name" || field === "url") {
            setFormErrors((current) => ({ ...current, [field]: undefined }));
          }
        }}
        onAddHeader={() =>
          setFormData((current) => ({
            ...current,
            headers: [...current.headers, { key: "", value: "" }],
          }))
        }
        onRemoveHeader={(index) =>
          setFormData((current) => ({
            ...current,
            headers: current.headers.filter((_, headerIndex) => headerIndex !== index),
          }))
        }
        onUpdateHeader={(index, field, value) =>
          setFormData((current) => ({
            ...current,
            headers: current.headers.map((header, headerIndex) =>
              headerIndex === index ? { ...header, [field]: value } : header,
            ),
          }))
        }
        onSubmit={() => void handleSubmit()}
      />
    </PageShell>
  );
};

export default CreateWebhook;
